/**
 * Invoicing Handlers
 *
 * Pure command handlers for the Invoicing subdomain. Ported from the ERPs
 * subdomain and adapted to the simplified `data / ui / loading` state shape:
 * provisioned integrations are keyed in `data.integrations`, the add modal is a
 * single boolean + selected connector, and submit/delete lifecycles are tracked
 * through `loading.submitting` and the integrations record respectively.
 */

import { InvoicingState } from './state';
import { InvoicingCommand } from './commands';
import { CommandHandlerMap } from '@commandDrivenStateEngine/core/types';

type ExtractCommand<T extends string> = Extract<InvoicingCommand, { type: T }>;

// List handlers
const handleListSet = (
  state: InvoicingState,
  cmd: ExtractCommand<'integrations/invoicing/list/set'>
): InvoicingState => ({
  ...state,
  data: { ...state.data, integrations: cmd.payload.integrations },
  // The arrival of the list snapshot ends the initial load.
  loading: { ...state.loading, listLoading: false }
});

// Add modal handlers
const handleAddModalOpen = ( state: InvoicingState ): InvoicingState => ({
  ...state,
  ui: { ...state.ui, addModalOpen: true, selectedConnectorId: null }
});

const handleAddModalClose = ( state: InvoicingState ): InvoicingState => ({
  ...state,
  ui: { ...state.ui, addModalOpen: false, selectedConnectorId: null },
  loading: { ...state.loading, submitting: false },
  pendingSubmission: null
});

const handleAddModalSelectConnector = (
  state: InvoicingState,
  cmd: ExtractCommand<'integrations/invoicing/addModal/selectConnector'>
): InvoicingState => ({
  ...state,
  ui: { ...state.ui, selectedConnectorId: cmd.payload.connectorId }
});

// Submit handlers
const handleSubmitStart = (
  state: InvoicingState,
  cmd: ExtractCommand<'integrations/invoicing/submit/start'>
): InvoicingState => ({
  ...state,
  loading: { ...state.loading, submitting: true },
  pendingSubmission: {
    connectorId: cmd.payload.connectorId,
    credentials: cmd.payload.credentials,
    label: cmd.payload.label
  }
});

const handleSubmitSuccess = (
  state: InvoicingState,
  cmd: ExtractCommand<'integrations/invoicing/submit/success'>
): InvoicingState => ({
  ...state,
  data: {
    ...state.data,
    integrations: {
      ...state.data.integrations,
      [cmd.payload.integration.id]: cmd.payload.integration
    }
  },
  ui: { ...state.ui, addModalOpen: false, selectedConnectorId: null },
  loading: { ...state.loading, submitting: false },
  pendingSubmission: null
});

const handleSubmitError = ( state: InvoicingState ): InvoicingState => ({
  ...state,
  loading: { ...state.loading, submitting: false },
  pendingSubmission: null
});

// Delete handlers
const handleDeleteStart = (
  state: InvoicingState,
  cmd: ExtractCommand<'integrations/invoicing/delete/start'>
): InvoicingState => ({
  ...state,
  loading: { ...state.loading, submitting: true },
  pendingDeletion: { integrationId: cmd.payload.integrationId }
});

const handleDeleteSuccess = (
  state: InvoicingState,
  cmd: ExtractCommand<'integrations/invoicing/delete/success'>
): InvoicingState => {
  const { [cmd.payload.integrationId]: _, ...rest } = state.data.integrations;
  return {
    ...state,
    data: { ...state.data, integrations: rest },
    loading: { ...state.loading, submitting: false },
    pendingDeletion: null
  };
};

const handleDeleteError = ( state: InvoicingState ): InvoicingState => ({
  ...state,
  loading: { ...state.loading, submitting: false },
  pendingDeletion: null
});

export const handlers: CommandHandlerMap<InvoicingState, InvoicingCommand> = {
  'integrations/invoicing/list/set': handleListSet,
  'integrations/invoicing/addModal/open': handleAddModalOpen,
  'integrations/invoicing/addModal/close': handleAddModalClose,
  'integrations/invoicing/addModal/selectConnector': handleAddModalSelectConnector,
  'integrations/invoicing/submit/start': handleSubmitStart,
  'integrations/invoicing/submit/success': handleSubmitSuccess,
  'integrations/invoicing/submit/error': handleSubmitError,
  'integrations/invoicing/delete/start': handleDeleteStart,
  'integrations/invoicing/delete/success': handleDeleteSuccess,
  'integrations/invoicing/delete/error': handleDeleteError
};
